import React, { useState } from 'react';
import { Alert, Button } from 'reactstrap';
import DateTimeText from '../../components/DateTimeText';

type OperationalIssue =
{
    key: string
    module: string
    severity: string
    title: string
    message: string
    resolutionHint?: string | null
    occurrenceCount?: number
    firstDetectedAtUtc?: string
    lastSeenAtUtc?: string
}

type OperationalIssuesPanelProperties =
{
    issues?: OperationalIssue[] | null
    title?: string
    emptyMessage?: string
    onResolve?: (key: string) => Promise<any>
    compact?: boolean
}

function OperationalIssuesPanel
(
    {
        issues,
        title = 'Aktywne problemy operacyjne',
        emptyMessage = 'Brak aktywnych problemów operacyjnych.',
        onResolve,
        compact = false
    }
    : OperationalIssuesPanelProperties
)
{
    const [resolvingKey, setResolvingKey] = useState<string | null>(null);
    const activeIssues = issues ?? [];

    if (activeIssues.length === 0)
    {
        return compact ? null : <Alert color="success">{emptyMessage}</Alert>;
    }

    const handleResolve = async (key: string) =>
    {
        if (!onResolve)
        {
            return;
        }

        setResolvingKey(key);

        try
        {
            await onResolve(key);
        }
        finally
        {
            setResolvingKey(null);
        }
    };

    return (
        <section className="mb-4">
            {title && <h5 className="mb-3">{title}</h5>}

            {activeIssues.map(issue => (
                <Alert key={issue.key} color={issue.severity === 'Error' ? 'danger' : 'warning'} className="mb-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-start">
                        <div className="pr-3">
                            <div className="font-weight-bold">{issue.title || issue.key}</div>
                            <div>{issue.message}</div>

                            {issue.resolutionHint && (
                                <div className="mt-2">
                                    <strong>Jak rozwiązać:</strong> {issue.resolutionHint}
                                </div>
                            )}

                            {!compact && (
                                <small className="d-block mt-2 text-muted">
                                    Klucz: {issue.key}, wystąpienia: {issue.occurrenceCount ?? 1}, ostatnio: <DateTimeText value={issue.lastSeenAtUtc} />
                                </small>
                            )}
                        </div>

                        {onResolve && (
                            <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                className="mt-2 mt-md-0"
                                disabled={resolvingKey === issue.key}
                                onClick={() => handleResolve(issue.key)}
                            >
                                {resolvingKey === issue.key ? 'Oznaczanie...' : 'Oznacz jako rozwiązany'}
                            </Button>
                        )}
                    </div>
                </Alert>
            ))}
        </section>
    );
}

export default OperationalIssuesPanel;
